import React, { useMemo, useState } from 'react';
import { Batiment, Vehicule } from '../../../../shared/types/game';
import { useVehicleCrewStatus } from '../hooks/useVehicleCrewStatus';
import { useStatusPalette } from '../hooks/useStatusPalette';
import EngagementGroupsPanel from './EngagementGroupsPanel';

/**
 * Étape 3 — Synoptique des moyens (tableau mural).
 * Une pastille par engin, aux couleurs de son statut, regroupable par statut,
 * par site ou par type. Le statut « disponible » est scindé grâce à l'armement
 * (Étape 1) : engin réellement armable (vert) vs « disponible matériel » (vert
 * foncé) quand l'équipage manque.
 */

interface Props {
  playerVehicles: Vehicule[];
  buildings: Batiment[];
}

type EffStatus = 'disponible' | 'materiel' | 'en_mission' | 'en_maintenance' | 'panne';
type GroupBy = 'statut' | 'site' | 'type';

const STATUS_ORDER: EffStatus[] = ['disponible', 'materiel', 'en_mission', 'en_maintenance', 'panne'];

const MoyensSynopticView: React.FC<Props> = ({ playerVehicles, buildings }) => {
  const [groupBy, setGroupBy] = useState<GroupBy>('statut');
  const [editPalette, setEditPalette] = useState(false);
  const { vehicles: crewStatus } = useVehicleCrewStatus();
  const { palette, setColor, resetAll, isCustomised } = useStatusPalette();

  // Carte engin → armable (pour scinder « disponible » / « disponible matériel »).
  const armableById = useMemo(() => {
    const m = new Map<number, boolean | null>();
    for (const c of crewStatus) m.set(Number(c.vehicleId), c.armable);
    return m;
  }, [crewStatus]);

  const buildingName = useMemo(() => {
    const map = new Map<number, string>();
    for (const b of buildings || []) map.set(Number(b.id), b.nom || `Centre #${b.id}`);
    return (id: number | undefined) => (id != null && map.has(id) ? map.get(id)! : 'Hors centre');
  }, [buildings]);

  const effStatus = useMemo(
    () =>
      (v: Vehicule): EffStatus => {
        if (v.statut === 'disponible') {
          return armableById.get(Number(v.id)) === false ? 'materiel' : 'disponible';
        }
        if (v.statut === 'en_mission') return 'en_mission';
        if (v.statut === 'en_maintenance') return 'en_maintenance';
        return 'panne';
      },
    [armableById],
  );

  // Types présents dans le parc + engins réellement engageables (disponibles ET armés),
  // qui alimentent la faisabilité des groupes d'engagement.
  const fleetTypes = useMemo(
    () => Array.from(new Set((playerVehicles || []).map((v) => v.type || 'Type inconnu'))).sort(),
    [playerVehicles],
  );
  const availableByType = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const v of playerVehicles || []) {
      if (effStatus(v) !== 'disponible') continue;
      const t = v.type || 'Type inconnu';
      acc[t] = (acc[t] || 0) + 1;
    }
    return acc;
  }, [playerVehicles, effStatus]);

  const groups = useMemo(() => {
    const map = new Map<string, Vehicule[]>();
    const keyOf = (v: Vehicule): string => {
      if (groupBy === 'site') return buildingName(v.batiment_id);
      if (groupBy === 'type') return v.type || 'Type inconnu';
      return palette[effStatus(v)].label;
    };
    for (const v of playerVehicles || []) {
      const k = keyOf(v);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(v);
    }
    return Array.from(map.entries()).sort((a, b) => {
      if (groupBy === 'statut') {
        const ia = STATUS_ORDER.findIndex((s) => palette[s].label === a[0]);
        const ib = STATUS_ORDER.findIndex((s) => palette[s].label === b[0]);
        return ia - ib;
      }
      return a[0].localeCompare(b[0]);
    });
  }, [playerVehicles, groupBy, effStatus, buildingName, palette]);

  return (
    <div className="flex h-full flex-col rounded-lg border border-slate-700/75 bg-slate-900/90 p-3 shadow-xl shadow-black/25">
      <div className="mb-2 flex shrink-0 flex-wrap items-center justify-between gap-2">
        <h2 className="text-[12px] font-semibold uppercase tracking-[0.12em] text-emerald-300">
          Synoptique des moyens — {playerVehicles.length} engin{playerVehicles.length > 1 ? 's' : ''}
        </h2>
        <div className="flex items-center gap-1 text-[10px]">
          <span className="text-slate-500">Regrouper :</span>
          {(['statut', 'site', 'type'] as GroupBy[]).map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGroupBy(g)}
              className={`rounded border px-2 py-0.5 font-semibold uppercase tracking-wide transition-colors ${
                groupBy === g
                  ? 'border-emerald-500/60 bg-emerald-500/20 text-emerald-100'
                  : 'border-slate-600/50 bg-slate-800/40 text-slate-300 hover:text-white'
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      {/* Légende — et palette éditable par le joueur */}
      <div className="mb-2 flex shrink-0 flex-wrap items-center gap-2 text-[9px]">
        {STATUS_ORDER.map((s) => (
          <span key={s} className="inline-flex items-center gap-1">
            <span
              className="rounded-sm border border-black/30 px-1 py-0.5 text-[9px] font-semibold"
              style={{ backgroundColor: palette[s].bg, color: palette[s].fg }}
            >
              {palette[s].label}
            </span>
          </span>
        ))}
        <button
          type="button"
          onClick={() => setEditPalette((v) => !v)}
          className="rounded border border-slate-600/50 bg-slate-800/50 px-1.5 py-0.5 text-[9px] text-slate-300 hover:bg-slate-700/70"
        >
          {editPalette ? 'Fermer' : '🎨 Couleurs'}
        </button>
      </div>

      {editPalette ? (
        <div className="mb-2 shrink-0 rounded-lg border border-slate-700/70 bg-slate-950/50 p-2">
          <div className="mb-1.5 flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              Palette des statuts
            </p>
            <button
              type="button"
              onClick={() => void resetAll()}
              className="rounded border border-slate-600/50 bg-slate-800/50 px-2 py-0.5 text-[9px] text-slate-300 hover:bg-slate-700/70"
            >
              ↺ Tout réinitialiser
            </button>
          </div>
          <p className="mb-2 text-[9px] text-slate-500">
            Le fond <em>et</em> l&apos;écriture comptent : deux statuts peuvent partager le même fond et ne se
            distinguer que par la couleur du texte.
          </p>
          <div className="space-y-1">
            {STATUS_ORDER.map((s) => (
              <div key={s} className="flex items-center justify-between gap-2">
                <span
                  className="min-w-[120px] rounded-sm border border-black/30 px-1.5 py-0.5 text-[10px] font-semibold"
                  style={{ backgroundColor: palette[s].bg, color: palette[s].fg }}
                >
                  {palette[s].label}
                </span>
                <div className="flex items-center gap-2 text-[9px] text-slate-400">
                  <label className="flex items-center gap-1">
                    Fond
                    <input
                      type="color"
                      value={palette[s].bg}
                      onChange={(e) => void setColor(s, 'bg', e.target.value)}
                      className="h-5 w-7 cursor-pointer rounded border border-slate-600/60 bg-transparent"
                    />
                  </label>
                  <label className="flex items-center gap-1">
                    Texte
                    <input
                      type="color"
                      value={palette[s].fg}
                      onChange={(e) => void setColor(s, 'fg', e.target.value)}
                      className="h-5 w-7 cursor-pointer rounded border border-slate-600/60 bg-transparent"
                    />
                  </label>
                  <button
                    type="button"
                    disabled={!isCustomised(s)}
                    onClick={() => {
                      void setColor(s, 'bg', null);
                      void setColor(s, 'fg', null);
                    }}
                    title="Revenir au défaut"
                    className="rounded border border-slate-600/50 bg-slate-800/50 px-1.5 py-0.5 text-slate-300 hover:bg-slate-700/70 disabled:opacity-30"
                  >
                    ↺
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
        {playerVehicles.length === 0 ? (
          <p className="py-8 text-center text-[11px] text-slate-500">Aucun engin.</p>
        ) : (
          groups.map(([key, list]) => (
            <div key={key}>
              <div className="mb-1 flex items-center gap-2">
                <h3 className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{key}</h3>
                <span className="rounded-full bg-slate-700/50 px-1.5 text-[9px] text-slate-300">{list.length}</span>
                <span className="h-px flex-1 bg-slate-700/50" />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {list.map((v) => {
                  const st = effStatus(v);
                  const style = palette[st];
                  return (
                    <div
                      key={v.id}
                      title={`${v.nom} · ${v.type} · ${style.label}`}
                      className="flex min-w-[74px] max-w-[120px] flex-col rounded border border-black/25 px-1.5 py-1 leading-tight shadow-sm"
                      style={{ backgroundColor: style.bg, color: style.fg }}
                    >
                      <span className="truncate text-[10px] font-bold">{v.nom}</span>
                      <span className="truncate text-[9px] opacity-80">{v.type}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-2 max-h-[38%] shrink-0 overflow-y-auto">
        <EngagementGroupsPanel fleetTypes={fleetTypes} availableByType={availableByType} />
      </div>
    </div>
  );
};

export default MoyensSynopticView;
